/**
 * Normalises every failure — HTTP, network, timeout, parse — into one shape.
 *
 * Components branch on `kind`, never on status codes, so adding a new backend
 * error code does not mean touching every call site.
 */

import type { ErrorEnvelope, ValidationDetails } from '@/types/api'

export type ApiErrorKind =
  | 'validation'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'gone'
  | 'rate_limited'
  | 'server'
  | 'network'
  | 'timeout'
  | 'cancelled'
  | 'unknown'

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly code: string
  readonly status: number
  /** field -> message, ready to hand to react-hook-form's setError. */
  readonly fields: Record<string, string>
  readonly details: unknown
  readonly correlationId: string | undefined
  readonly retryAfter: number | undefined

  constructor(init: {
    kind: ApiErrorKind
    code: string
    message: string
    status: number
    fields?: Record<string, string>
    details?: unknown
    correlationId?: string
    retryAfter?: number
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.kind = init.kind
    this.code = init.code
    this.status = init.status
    this.fields = init.fields ?? {}
    this.details = init.details
    this.correlationId = init.correlationId
    this.retryAfter = init.retryAfter
  }

  /** True when the backend's own message is safe and useful to show verbatim. */
  get hasActionableMessage(): boolean {
    return this.kind !== 'server' && this.kind !== 'unknown' && this.message.length > 0
  }
}

const KIND_BY_STATUS: Record<number, ApiErrorKind> = {
  400: 'validation',
  401: 'unauthenticated',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  410: 'gone',
  422: 'validation',
  429: 'rate_limited',
}

/**
 * Generic copy used only when the backend could not give us something better.
 * Anything the backend phrases for a human is preferred over these.
 */
const FALLBACK_MESSAGE: Record<ApiErrorKind, string> = {
  validation: 'Some of the details need correcting.',
  unauthenticated: 'Your session has expired. Sign in again to continue.',
  forbidden: 'You do not have permission to do that.',
  not_found: 'That item no longer exists. It may have been deleted.',
  conflict: 'That action conflicts with the current state of the record.',
  gone: 'That link has expired.',
  rate_limited: 'Too many attempts. Wait a moment and try again.',
  server: 'The server could not complete this request. Nothing was changed.',
  network: 'Cannot reach the server. Check your connection and try again.',
  timeout: 'The server took too long to respond. Nothing was changed.',
  cancelled: 'Request cancelled.',
  unknown: 'Something unexpected happened.',
}

function kindForStatus(status: number): ApiErrorKind {
  return KIND_BY_STATUS[status] ?? (status >= 500 ? 'server' : 'unknown')
}

function extractFields(details: unknown): Record<string, string> {
  if (!details || typeof details !== 'object') return {}
  const validation = details as ValidationDetails

  // Preferred: the backend's normalised map.
  if (validation.fields && typeof validation.fields === 'object') {
    return { ...validation.fields }
  }

  // Fallback for older payloads: rebuild from Pydantic's `loc` tuples.
  if (Array.isArray(validation.errors)) {
    const out: Record<string, string> = {}
    for (const entry of validation.errors) {
      const parts = entry.loc ?? []
      const trimmed = parts[0] === 'body' ? parts.slice(1) : parts
      const field = entry.field ?? (trimmed.length ? trimmed.join('.') : '__root__')
      if (!(field in out)) out[field] = entry.msg ?? 'Invalid value'
    }
    return out
  }
  return {}
}

/** Build an ApiError from a parsed HTTP error response. */
export function fromResponse(
  status: number,
  body: unknown,
  headers?: Headers,
  correlationId?: string,
): ApiError {
  const kind = kindForStatus(status)
  const envelope = (body ?? {}) as Partial<ErrorEnvelope>
  const error = envelope.error

  const retryAfterHeader = headers?.get('Retry-After')
  const retryAfter = retryAfterHeader ? Number(retryAfterHeader) : undefined

  // A 500 message is for logs, not users — never surface the backend's wording.
  const message =
    kind === 'server' || !error?.message ? FALLBACK_MESSAGE[kind] : error.message

  return new ApiError({
    kind,
    code: error?.code ?? String(status),
    message,
    status,
    fields: kind === 'validation' ? extractFields(error?.details) : {},
    details: error?.details,
    correlationId: envelope.correlation_id ?? correlationId,
    retryAfter: Number.isFinite(retryAfter) ? retryAfter : undefined,
  })
}

/** Build an ApiError from a thrown fetch/abort failure. */
export function fromThrown(cause: unknown, timedOut: boolean): ApiError {
  if (cause instanceof ApiError) return cause

  if (cause instanceof DOMException && cause.name === 'AbortError') {
    const kind: ApiErrorKind = timedOut ? 'timeout' : 'cancelled'
    return new ApiError({ kind, code: kind, message: FALLBACK_MESSAGE[kind], status: 0 })
  }

  return new ApiError({
    kind: 'network',
    code: 'network_error',
    message: FALLBACK_MESSAGE.network,
    status: 0,
  })
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

/** Message for a toast or alert, with a support reference when we have nothing useful. */
export function toUserMessage(error: unknown): string {
  if (!isApiError(error)) {
    return error instanceof Error && error.message ? error.message : FALLBACK_MESSAGE.unknown
  }
  if (error.hasActionableMessage) return error.message
  return error.correlationId
    ? `${error.message} (reference ${error.correlationId.slice(0, 8)})`
    : error.message
}
